import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TaskPriority } from './tasksApi';

// ---------------------------------------------------------------------------
// Task Templates API — mirrors backend/src/routes/taskTemplates.ts (CLE-260).
// A template is a named, ordered list of task blueprints (optionally tied to a
// department) that can be applied to a project to create its tasks in bulk.
// ---------------------------------------------------------------------------

export interface TemplateDepartmentRef {
  id: string;
  name: string;
  color: string;
}

export interface TemplateLabelRef {
  id: string;
  name: string;
  color: string;
}

export interface TaskTemplateItem {
  id: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  /** Decimal hours, like a task's estimate; shown as "1h 30m". */
  estimate: number | null;
  departmentId: string | null;
  department: TemplateDepartmentRef | null;
  /** Labels the created task starts with (the global task labels). */
  labels: TemplateLabelRef[];
  order: number;
}

export interface TaskTemplate {
  id: string;
  name: string;
  description: string | null;
  departmentId: string | null;
  department: TemplateDepartmentRef | null;
  itemCount: number;
  items: TaskTemplateItem[];
  createdAt: string;
  updatedAt: string;
}

export interface TaskTemplateItemInput {
  title: string;
  description?: string | null;
  priority?: TaskPriority;
  estimate?: number | null;
  departmentId?: string | null;
  labelIds?: string[];
}

export interface TaskTemplateInput {
  name: string;
  description?: string | null;
  departmentId?: string | null;
  items: TaskTemplateItemInput[];
}

export interface ApplyTemplateResult {
  templateId: string;
  templateName: string;
  projectId: string;
  /** Ids of the tasks the apply created, in template order. */
  createdTaskIds: string[];
  createdCount: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /api/task-templates — all templates with their items. */
export async function listTaskTemplates(): Promise<TaskTemplate[]> {
  const res = await apiFetch<Envelope<TaskTemplate[]>>('/api/task-templates', {
    headers: authHeaders(),
  });
  return res.data;
}

/** GET /api/task-templates/:id — a single template with items. */
export async function getTaskTemplate(id: string): Promise<TaskTemplate> {
  const res = await apiFetch<Envelope<TaskTemplate>>(`/api/task-templates/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** POST /api/task-templates — create a template (settings.canEditTaskTemplates). */
export async function createTaskTemplate(input: TaskTemplateInput): Promise<TaskTemplate> {
  const res = await apiFetch<Envelope<TaskTemplate>>('/api/task-templates', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/**
 * PUT /api/task-templates/:id — update metadata and replace the item list
 * wholesale (the backend swaps items in a transaction).
 */
export async function updateTaskTemplate(
  id: string,
  input: TaskTemplateInput,
): Promise<TaskTemplate> {
  const res = await apiFetch<Envelope<TaskTemplate>>(`/api/task-templates/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** DELETE /api/task-templates/:id — delete a template (items cascade). */
export async function deleteTaskTemplate(id: string): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(
    `/api/task-templates/${id}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

/** POST /api/task-templates/:id/apply — create the template's tasks in a project. */
export async function applyTaskTemplate(
  id: string,
  projectId: string,
): Promise<ApplyTemplateResult> {
  const res = await apiFetch<Envelope<ApplyTemplateResult>>(`/api/task-templates/${id}/apply`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ projectId }),
  });
  return res.data;
}
